import { useFormik } from "formik";
import { FC, useState } from "react";
import { Button, Checkbox, Input, Select, Text } from "rizzui";
import { toast } from "sonner";
import * as yup from "yup";
import { HeadquarterAssetService } from "../../../../../services/headquarters/headquarterAssetService";
import { HeadquarterAsset } from "../../../../../type/entities/headquarter-asset-type";
import useHandleErrors from "../../../../../hooks/useHandleErrors";

interface HeadquarterAssetFormProps {
    setOpenModal: (isOpen: boolean) => void;
    refetch: () => void;
    asset: HeadquarterAsset | null;
    headquarterId: string;
}

const HeadquarterAssetForm: FC<HeadquarterAssetFormProps> = ({ setOpenModal, refetch, asset, headquarterId }) => {
    const [loading, setLoading] = useState(false);
    const service = new HeadquarterAssetService();
    const { handleErrors } = useHandleErrors();

    const initialValues = {
        type: asset?.type || 'CONTAINER',
        name: asset?.name || '',
        quantity: asset?.quantity || 1,
        isOwned: asset?.isOwned || false,
    };

    const schema = yup.object({
        type: yup.string().required('El tipo es obligatorio'),
        name: yup.string().required('El nombre/tipo es obligatorio'),
        quantity: yup.number().required('La cantidad es obligatoria').min(1, 'La cantidad debe ser al menos 1'),
        isOwned: yup.boolean(),
    });

    const formik = useFormik({
        initialValues,
        validationSchema: schema,
        onSubmit: async (values) => {
            setLoading(true);
            try {
                const payload = {
                    ...values,
                    headquarterId: headquarterId,
                    id: asset?.id,
                };

                let response;
                if (asset) {
                    response = await service.editAsset(payload);
                } else {
                    response = await service.createAsset(payload);
                }

                const responseData = response.getResponseData();

                if (responseData.success) {
                    toast.success(asset ? 'Activo actualizado correctamente' : 'Activo creado correctamente');
                    refetch();
                    setOpenModal(false);
                } else {
                    handleErrors(responseData);
                }
            } catch (error) {
                handleErrors(error);
            } finally {
                setLoading(false);
            }
        },
    });

    const typeOptions = [
        { label: 'Contenedor', value: 'CONTAINER' },
        { label: 'Filtro', value: 'FILTER' },
    ];

    return (
        <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4 p-4">
            <Select
                label="Tipo"
                options={typeOptions}
                value={typeOptions.find(o => o.value === formik.values.type)}
                onChange={(option: any) => formik.setFieldValue('type', option.value)}
                error={formik.errors.type}
            />

            <Input
                label="Nombre / Descripción"
                placeholder="Ej: Contenedor 1000L o Filtro Aceite"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.errors.name}
            />

            <Input
                label="Cantidad"
                type="number"
                name="quantity"
                value={formik.values.quantity}
                onChange={formik.handleChange}
                error={formik.errors.quantity}
            />

            <div className="flex items-center gap-2 mt-2">
                <Checkbox
                    label="¿En propiedad?"
                    name="isOwned"
                    checked={formik.values.isOwned}
                    onChange={formik.handleChange}
                />
            </div>

            <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setOpenModal(false)} disabled={loading}>
                    Cancelar
                </Button>
                <Button type="submit" isLoading={loading}>
                    {asset ? 'Guardar Cambios' : 'Crear Activo'}
                </Button>
            </div>
        </form>
    );
};

export default HeadquarterAssetForm;
